import React, { useEffect, useState, useMemo } from "react";
import { useAccelContext } from "../../contexts/AccelContext/AccelContext";
import { useAccountContext } from "../../contexts/AccountsContext/AccountsContext";
import { AreaChart, Area, XAxis, Tooltip, ResponsiveContainer } from "recharts";
import { CircularProgress, Box } from "@mui/material"; // Updated casing
import { TransactionsByMonth } from "../../types/Transaction";
import { format, subMonths } from "date-fns";
// Custom Tooltip Component

export const AccelAreaChart = () => {
  const { fillerData, theme, transactionsByMonth } = useAccelContext();
  const { accounts, total } = useAccountContext();
  const data: TransactionsByMonth[] = Array.isArray(transactionsByMonth)
    ? transactionsByMonth
    : [];
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  useEffect(() => {
    // Set isFirstLoad to false after the first render
    setIsFirstLoad(false);
  }, [data, accounts, fillerData, total]);

  const chartData = useMemo(() => {
    if (!data || !accounts) return [];

    const currentDate = new Date();
    const totalBalance = total;

    // Create an array of the last 7 months
    const last7Months = Array.from({ length: 7 }, (_, i) => {
      const date = subMonths(currentDate, i);
      return { year: date.getFullYear(), month: date.getMonth() + 1 };
    }).reverse();

    // Calculate cumulative balance for each month
    let cumulativeBalance = totalBalance;
    return last7Months.map(({ year, month }) => {
      const monthData = data.find((d) => d.year === year && d.month === month);
      if (monthData) {
        cumulativeBalance -= monthData.delta;
      }
      return {
        year,
        month,
        balance: cumulativeBalance,
      };
    });
  }, [data, accounts, fillerData, total]);

  if (!data || !accounts) {
    return <CircularProgress />;
  }

  const monthFormatter = (month: number, year: number) => {
    const date = new Date(year, month - 1);
    return format(date, "MMM");
  };

  return (
    <ResponsiveContainer
      width="100%"
      aspect={2.5}
      style={{ background: theme?.accent?.hex || "#000000" }}
    >
      <AreaChart
        data={chartData}
        margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
      >
        <defs>
          <linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={theme?.primary?.hex} stopOpacity={1} />
            <stop
              offset="100%"
              stopColor={theme?.primary?.hex}
              stopOpacity={1}
            />
          </linearGradient>
          <linearGradient id="colorBg" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={theme?.primary?.hex} stopOpacity={1} />
            <stop
              offset="30%"
              stopColor={theme?.primary?.hex}
              stopOpacity={1}
            />
            <stop offset="80%" stopColor={theme?.accent?.hex} stopOpacity={1} />
            <stop
              offset="100%"
              stopColor={theme?.accent?.hex}
              stopOpacity={1}
            />
          </linearGradient>
        </defs>
        <XAxis
          dataKey="month"
          tickFormatter={(month, index) =>
            monthFormatter(month, chartData[index].year)
          }
          tick={{
            fontSize: 14,
            fontFamily: "Helvetica Neue",
            fill: theme?.secondary?.hex,
            textAnchor: "middle",
          }}
          tickCount={7}
          tickLine={false}
          axisLine={{ stroke: theme?.secondary?.hex, strokeWidth: 1 }}
          interval={0}
          padding={{ left: 30, right: 30 }}
        />
        <Tooltip />
        <Area
          type="monotone"
          dataKey="balance"
          stroke={theme?.primary?.hex}
          strokeWidth={2}
          fill="url(#colorBg)"
          activeDot={{
            stroke: theme?.primary?.hex,
            strokeWidth: 3,
            r: 5,
          }}
          isAnimationActive={isFirstLoad} // Animation only on first load
        />
      </AreaChart>
    </ResponsiveContainer>
  );
};

export default AccelAreaChart;
